import { Close as CloseIcon, OpenInNewOutlined as OpenInNewOutlinedIcon } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MuiDrawer from '@mui/material/Drawer';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import is from '@sindresorhus/is';
import type { FunctionComponent, ReactNode } from 'react';
import { useContext } from 'react';
import { useSearchParams } from 'react-router';

import { ApplicationContext } from '~/shared/context/context';

import { SecondaryActionButton } from '../../../../design/components/button/SecondaryAction';
import { ComponentErrorBoundary } from '../../../error-handling/ErrorBoundaries';
import { userProfileUrl } from '../constants';
import { HeaderBackground } from '../user-profile';
import { CandidatePreview } from './candidate-preview';
import { GenericPreview } from './generic-preview';
import { route } from './route';

const drawers = {
    candidate: CandidatePreview,
    generic: GenericPreview,
};

type PreviewDrawerType = keyof typeof drawers;

const isPreviewDrawerType = (type: string): type is PreviewDrawerType => Object.keys(drawers).includes(type);

type ProfilePreview = {
    readonly closeProfilePreview: () => void;
    readonly openProfilePreview: (userId: string) => void;
};

export const useProfilePreview = (previewType: PreviewDrawerType = 'generic'): ProfilePreview => {
    const [searchParams, setSearchParams] = useSearchParams();

    const openProfilePreview = (userId: string): void => {
        const profilePreviewParams = new URLSearchParams(searchParams);
        profilePreviewParams.set(route.searchParams.profilePreviewUserId, userId);
        profilePreviewParams.set(route.searchParams.profilePreviewType, previewType);
        setSearchParams(profilePreviewParams, { replace: true });
    };

    const closeProfilePreview = (): void => {
        const profilePreviewParams = new URLSearchParams(searchParams);
        profilePreviewParams.delete(route.searchParams.profilePreviewUserId);
        profilePreviewParams.delete(route.searchParams.profilePreviewType);
        setSearchParams(profilePreviewParams, { replace: true });
    };

    return { closeProfilePreview, openProfilePreview };
};

type DrawerProps = { readonly content: ReactNode };

const Drawer: FunctionComponent<DrawerProps> = (props) => {
    const { actions, applicationState, translate } = useContext(ApplicationContext);
    const { closeProfilePreview } = useProfilePreview();
    const profilePreviewUserId = applicationState.locationSearchParams.get(route.searchParams.profilePreviewUserId);

    const isProfilePreviewUserIdAvailable = is.null(profilePreviewUserId) === false;

    const navigateToUserProfile = (): void => actions.navigateTo(userProfileUrl(profilePreviewUserId!));

    return (
        <MuiDrawer
            anchor="right"
            open={isProfilePreviewUserIdAvailable}
            PaperProps={{ sx: { width: 'clamp(900px, 66vw, 1080px)' } }}
            onClose={closeProfilePreview}
        >
            <Grid alignItems="center" container={true} justifyContent="space-between" minHeight={64} paddingX={3}>
                <Grid>
                    <Typography variant="h5">{translate('pages.profile-preview.title')}</Typography>
                </Grid>
                <Grid>
                    <SecondaryActionButton
                        isDisabled={!isProfilePreviewUserIdAvailable}
                        label={translate('pages.profile-preview.open-profile')}
                        startIcon={<OpenInNewOutlinedIcon />}
                        onClick={navigateToUserProfile}
                    />
                    <IconButton onClick={closeProfilePreview}>
                        <CloseIcon />
                    </IconButton>
                </Grid>
            </Grid>
            <Divider />
            <ComponentErrorBoundary message="errors.component.drawer.profile-preview">
                <Box overflow="auto" paddingX={3} py={1}>
                    <HeaderBackground />
                    <Box px={1} sx={{ mt: -12 }} width="100%">
                        {props.content}
                    </Box>
                </Box>
            </ComponentErrorBoundary>
        </MuiDrawer>
    );
};

export const ProfilePreview: FunctionComponent = () => {
    const { applicationState } = useContext(ApplicationContext);

    const profilePreviewType = applicationState.locationSearchParams.get(route.searchParams.profilePreviewType) ?? '';

    const DrawerContent = isPreviewDrawerType(profilePreviewType) ? drawers[profilePreviewType] : drawers.generic;

    return <Drawer content={<DrawerContent />} />;
};
